import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import P2pLayout from './P2pLayout';
import AuthService from '../../../api/services/AuthService';
import { alertSuccessMessage, alertErrorMessage } from '../../../customComponents/CustomAlertMessage';
import LoaderHelper from '../../../customComponents/Loading/LoaderHelper';
import P2pFindAdModal from './P2pFindAdModal';
import './p2p.css';

const DEFAULT_MOCK_ADS = [
  {
    _id: 'mock-1',
    adNo: 'AD-100238',
    side: 'BUY',
    asset: 'USDT',
    fiat: 'INR',
    totalAmount: '12,350.00',
    completedOrders: 156,
    minLimit: '200.00',
    maxLimit: '2,000,000.00',
    price: '95.04',
    exchangeRate: '95.04',
    paymentMethods: [
      { name: 'Bank Transfer (India)', color: '#94A3B8' },
      { name: 'UPI', color: '#F97316' }
    ],
    lastUpdated: '2025-05-28 14:30',
    createTime: '2025-05-28 10:15',
    isOnline: true
  },
  {
    _id: 'mock-2',
    adNo: 'AD-100237',
    side: 'BUY',
    asset: 'USDT',
    fiat: 'INR',
    totalAmount: '8,750.00',
    completedOrders: 98,
    minLimit: '500.00',
    maxLimit: '1,500,000.00',
    price: '94.80',
    exchangeRate: '94.80',
    paymentMethods: [
      { name: 'Google Pay (GPay)', color: '#3B82F6' },
      { name: 'PhonePe', color: '#8B5CF6' }
    ],
    lastUpdated: '2025-05-27 18:45',
    createTime: '2025-05-27 09:20',
    isOnline: true
  },
  {
    _id: 'mock-3',
    adNo: 'AD-100236',
    side: 'SELL',
    asset: 'USDT',
    fiat: 'INR',
    totalAmount: '5,420.00',
    completedOrders: 72,
    minLimit: '300.00',
    maxLimit: '1,000,000.00',
    price: '96.10',
    exchangeRate: '96.10',
    paymentMethods: [
      { name: 'Bank Transfer (India)', color: '#94A3B8' },
      { name: 'Paytm', color: '#0284C7' }
    ],
    lastUpdated: '2025-05-26 16:20',
    createTime: '2025-05-26 08:10',
    isOnline: false
  },
  {
    _id: 'mock-4',
    adNo: 'AD-100235',
    side: 'BUY',
    asset: 'USDT',
    fiat: 'INR',
    totalAmount: '15,300.00',
    completedOrders: 210,
    minLimit: '200.00',
    maxLimit: '2,500,000.00',
    price: '94.95',
    exchangeRate: '94.95',
    paymentMethods: [
      { name: 'UPI', color: '#F97316' },
      { name: 'Bank Transfer (India)', color: '#94A3B8' }
    ],
    lastUpdated: '2025-05-25 11:05',
    createTime: '2025-05-25 07:30',
    isOnline: true
  },
  {
    _id: 'mock-5',
    adNo: 'AD-100234',
    side: 'SELL',
    asset: 'USDT',
    fiat: 'INR',
    totalAmount: '3,250.00',
    completedOrders: 45,
    minLimit: '200.00',
    maxLimit: '800,000.00',
    price: '96.50',
    exchangeRate: '96.50',
    paymentMethods: [
      { name: 'Google Pay (GPay)', color: '#3B82F6' },
      { name: 'UPI', color: '#F97316' }
    ],
    lastUpdated: '2025-05-24 12:40',
    createTime: '2025-05-24 06:45',
    isOnline: true
  }
];

const P2pMyAds = () => {
  const navigate = useNavigate();

  // Active Category Tab
  const [activeTab, setActiveTab] = useState('Normal Ads');
  const tabs = ['Normal Ads', 'Cash Ads', 'Block Ads', 'Fiat Ads', 'Premium Ads'];

  // Filters
  const [assetFilter, setAssetFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All'); // 'All' | 'Buy' | 'Sell'
  const [statusFilter, setStatusFilter] = useState('All'); // 'All' | 'Online' | 'Offline'
  const [startDate, setStartDate] = useState('2025-05-01');
  const [endDate, setEndDate] = useState('2025-05-31');
  const [isFindAdModalOpen, setIsFindAdModalOpen] = useState(false);

  // Ad selection state
  const [selectedAdIds, setSelectedAdIds] = useState([]);

  // Ads data
  const [adsList, setAdsList] = useState(DEFAULT_MOCK_ADS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Fetch from API with graceful fallback to mock data
  const fetchAds = useCallback(async () => {
    try {
      const params = {
        page,
        limit: pageSize,
        ...(typeFilter !== 'All' && { side: typeFilter.toUpperCase() }),
        ...(statusFilter !== 'All' && { status: statusFilter === 'Online' ? 'OPEN' : 'CLOSED' })
      };
      const res = await AuthService.getUserAds(params);
      if (res?.success && res?.data?.ads && res.data.ads.length > 0) {
        // Map backend ads to view schema
        const mapped = res.data.ads.map(ad => ({
          _id: ad._id,
          adNo: `AD-${(ad._id || '').slice(-6).toUpperCase() || '100238'}`,
          side: ad.side || 'BUY',
          asset: ad.qouteCurrency || 'USDT',
          fiat: ad.fiatCurrency || 'INR',
          totalAmount: Number(ad.volume || 0).toLocaleString(undefined, { minimumFractionDigits: 2 }),
          completedOrders: ad.completedOrders || 0,
          minLimit: Number(ad.minLimit || 200).toLocaleString(undefined, { minimumFractionDigits: 2 }),
          maxLimit: Number(ad.maxLimit || 2000000).toLocaleString(undefined, { minimumFractionDigits: 2 }),
          price: Number(ad.fixedPrice || 95.04).toFixed(2),
          exchangeRate: Number(ad.fixedPrice || 95.04).toFixed(2),
          paymentMethods: (ad.paymentDetails && ad.paymentDetails.length > 0)
            ? ad.paymentDetails.map(p => ({
                name: p.name || p.type || 'Bank Transfer',
                color: (p.type || '').includes('UPI') ? '#F97316' : '#94A3B8'
              }))
            : [{ name: 'Bank Transfer (India)', color: '#94A3B8' }, { name: 'UPI', color: '#F97316' }],
          lastUpdated: new Date(ad.updatedAt || Date.now()).toISOString().slice(0, 16).replace('T', ' '),
          createTime: new Date(ad.createdAt || Date.now()).toISOString().slice(0, 16).replace('T', ' '),
          isOnline: ad.isOnline !== undefined ? ad.isOnline : true
        }));
        setAdsList(mapped);
      }
    } catch (_) {
      // Keep mock ads if backend has no records yet
    }
  }, [page, pageSize, typeFilter, statusFilter]);

  useEffect(() => {
    fetchAds();
  }, [fetchAds]);

  // Filter local list
  const filteredAds = adsList.filter(ad => {
    if (assetFilter !== 'All' && ad.asset !== assetFilter) return false;
    if (typeFilter !== 'All' && ad.side.toLowerCase() !== typeFilter.toLowerCase()) return false;
    if (statusFilter !== 'All') {
      if (statusFilter === 'Online' && !ad.isOnline) return false;
      if (statusFilter === 'Offline' && ad.isOnline) return false;
    }
    return true;
  });

  // Checkbox handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedAdIds(filteredAds.map(ad => ad._id));
    } else {
      setSelectedAdIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedAdIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  // Reset filters
  const handleReset = () => {
    setAssetFilter('All');
    setTypeFilter('All');
    setStatusFilter('All');
    setStartDate('2025-05-01');
    setEndDate('2025-05-31');
    setSelectedAdIds([]);
  };

  // Batch actions
  const handlePublishAll = () => {
    if (selectedAdIds.length === 0) {
      alertErrorMessage('Please select ads to publish');
      return;
    }
    LoaderHelper.loaderStatus(true);
    setTimeout(() => {
      setAdsList(prev =>
        prev.map(ad => (selectedAdIds.includes(ad._id) ? { ...ad, isOnline: true } : ad))
      );
      LoaderHelper.loaderStatus(false);
      alertSuccessMessage('Selected ads published successfully');
      setSelectedAdIds([]);
    }, 400);
  };

  const handleTakeAllOffline = () => {
    if (selectedAdIds.length === 0) {
      alertErrorMessage('Please select ads to take offline');
      return;
    }
    LoaderHelper.loaderStatus(true);
    setTimeout(() => {
      setAdsList(prev =>
        prev.map(ad => (selectedAdIds.includes(ad._id) ? { ...ad, isOnline: false } : ad))
      );
      LoaderHelper.loaderStatus(false);
      alertSuccessMessage('Selected ads taken offline');
      setSelectedAdIds([]);
    }, 400);
  };

  const allSelected = filteredAds.length > 0 && selectedAdIds.length === filteredAds.length;

  return (
    <P2pLayout title="My Ads">
      <div className="p2p-my-ads-page">

        {/* ==================== 1. TOP HEADER ==================== */}
        <div className="p2p-my-ads-top-header">
          <div className="p2p-my-ads-header-info">
            <h2>My Ads</h2>
            <p>Manage and track all your P2P ads in one place.</p>
          </div>
          <button 
            type="button" 
            className="p2p-post-new-ad-btn"
            onClick={() => setIsFindAdModalOpen(true)}
          >
            <i className="ri-add-line"></i>
            <span>Post new ad</span>
          </button>
        </div>

        {/* ==================== 2. TABS BAR ==================== */}
        <div className="p2p-my-ads-tabs-bar">
          {tabs.map(tab => (
            <button
              key={tab}
              type="button"
              className={`p2p-my-ads-tab-btn ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* ==================== 3. FILTER CONTROLS BAR ==================== */}
        <div className="p2p-my-ads-filters-bar">
          <div className="p2p-my-ads-filters-left">
            {/* Asset/Type Filter */}
            <div className="p2p-my-ads-filter-dropdown">
              <span className="p2p-filter-prefix">Asset/Type</span>
              <select value={assetFilter} onChange={e => setAssetFilter(e.target.value)}>
                <option value="All">All</option>
                <option value="USDT">USDT</option>
                <option value="BTC">BTC</option>
                <option value="ETH">ETH</option>
              </select>
              <i className="ri-arrow-down-s-line"></i>
            </div>

            {/* Type Filter */}
            <div className="p2p-my-ads-filter-dropdown">
              <span className="p2p-filter-prefix">Type</span>
              <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                <option value="All">All</option>
                <option value="Buy">Buy</option>
                <option value="Sell">Sell</option>
              </select>
              <i className="ri-arrow-down-s-line"></i>
            </div>

            {/* Status Filter */}
            <div className="p2p-my-ads-filter-dropdown">
              <span className="p2p-filter-prefix">Status</span>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="All">All</option>
                <option value="Online">Online</option>
                <option value="Offline">Offline</option>
              </select>
              <i className="ri-arrow-down-s-line"></i>
            </div>

            {/* Date Range Box */}
            <div className="p2p-my-ads-date-range-box">
              <input 
                type="text" 
                value={`${startDate}  ~  ${endDate}`} 
                readOnly 
              />
              <i className="ri-calendar-line p2p-date-icon"></i>
            </div>

            {/* Reset Button */}
            <button 
              type="button" 
              className="p2p-my-ads-reset-btn"
              onClick={handleReset}
            >
              Reset
            </button>
          </div>

          <div className="p2p-my-ads-filters-right">
            <button type="button" className="p2p-ad-history-btn">
              <i className="ri-history-line"></i>
              <span>Ad History</span>
            </button>
          </div>
        </div>

        {/* ==================== 4. BATCH ACTIONS BAR ==================== */}
        <div className="p2p-my-ads-batch-bar">
          <label className="p2p-my-ads-select-all">
            <input 
              type="checkbox" 
              checked={allSelected}
              onChange={handleSelectAll}
            />
            <span>Select all ({filteredAds.length})</span>
          </label>

          <button 
            type="button" 
            className="p2p-batch-publish-btn"
            onClick={handlePublishAll}
          >
            Publish
          </button>

          <button 
            type="button" 
            className="p2p-batch-offline-btn"
            onClick={handleTakeAllOffline}
          >
            Take all offline
          </button>
        </div>

        {/* ==================== 5. ADS TABLE ==================== */}
        <div className="p2p-my-ads-table-container">
          <table className="p2p-my-ads-table">
            <thead>
              <tr>
                <th className="th-checkbox"></th>
                <th>Ad No.<br /><span className="th-sub">Type / Asset</span></th>
                <th>Total Amount<br /><span className="th-sub">Completed / Order Limit</span></th>
                <th>Price<br /><span className="th-sub">Exchange Rate</span></th>
                <th>Payment Method</th>
                <th>Last Updated<br /><span className="th-sub">Create Time</span></th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredAds.map((ad) => (
                <tr key={ad._id}>
                  {/* Checkbox */}
                  <td className="td-checkbox">
                    <input 
                      type="checkbox" 
                      checked={selectedAdIds.includes(ad._id)}
                      onChange={() => handleSelectOne(ad._id)}
                    />
                  </td>

                  {/* Ad No & Type / Asset */}
                  <td className="td-ad-no">
                    <strong className="p2p-ad-number">{ad.adNo}</strong>
                    <span className="p2p-ad-subtext">
                      {ad.side === 'BUY' ? 'Buy' : 'Sell'} / {ad.asset}
                    </span>
                  </td>

                  {/* Total Amount & Completed / Limit */}
                  <td className="td-amount">
                    <strong className="p2p-amount-val">{ad.totalAmount} {ad.asset}</strong>
                    <span className="p2p-ad-subtext">
                      {ad.completedOrders} / {ad.minLimit} ~ {ad.maxLimit} {ad.fiat}
                    </span>
                  </td>

                  {/* Price & Exchange Rate */}
                  <td className="td-price">
                    <strong className="p2p-price-val">{ad.price} {ad.fiat}</strong>
                    <span className="p2p-ad-subtext">
                      1 {ad.asset} ≈ {ad.exchangeRate} {ad.fiat}
                    </span>
                  </td>

                  {/* Payment Method Bars */}
                  <td className="td-payments">
                    <div className="p2p-payments-list">
                      {ad.paymentMethods.map((pm, i) => (
                        <div key={i} className="p2p-pm-item">
                          <span 
                            className="p2p-pm-bar" 
                            style={{ backgroundColor: pm.color }}
                          />
                          <span className="p2p-pm-text">{pm.name}</span>
                        </div>
                      ))}
                    </div>
                  </td>

                  {/* Last Updated & Create Time */}
                  <td className="td-times">
                    <strong className="p2p-updated-time">{ad.lastUpdated}</strong>
                    <span className="p2p-ad-subtext">{ad.createTime}</span>
                  </td>

                  {/* Status Badge */}
                  <td className="td-status">
                    {ad.isOnline ? (
                      <span className="p2p-status-pill online">
                        ● Online
                      </span>
                    ) : (
                      <span className="p2p-status-pill offline">
                        ● Offline
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ==================== 6. FOOTER / PAGINATION ==================== */}
        <div className="p2p-my-ads-footer">
          <div className="p2p-my-ads-footer-left">
            <span>Showing 1 to {filteredAds.length} of {filteredAds.length} ads</span>
          </div>

          <div className="p2p-my-ads-pagination">
            <button 
              type="button" 
              className="p2p-pag-arrow-btn"
              disabled={page <= 1}
              onClick={() => setPage(prev => Math.max(1, prev - 1))}
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            <button 
              type="button" 
              className="p2p-pag-num-btn active"
            >
              1
            </button>
            <button 
              type="button" 
              className="p2p-pag-arrow-btn"
              disabled
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>

            <div className="p2p-page-size-select">
              <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
              </select>
              <i className="ri-arrow-down-s-line"></i>
            </div>
          </div>
        </div>

      </div>

      {/* Find an Ad Popup Modal */}
      <P2pFindAdModal
        isOpen={isFindAdModalOpen}
        onClose={() => setIsFindAdModalOpen(false)}
      />
    </P2pLayout>
  );
};

export default P2pMyAds;
